import { makeAutoObservable, observable } from 'mobx'

/* eslint-disable-next-line import-x/no-restricted-paths -- FIXME: UXP-4500 */
import { ADDON_IDS } from '@src/app/billing/addon/constants'
import { DisposeBag } from '@src/lib/DisposeBag/DisposeBag'
import StatefulPromise from '@src/lib/StatefulPromise'
import type Service from '@src/service'
import PersistedCollection from '@src/service/collections/PersistedCollection'
import {
  AddonTier,
  type AddonTierDataRaw,
  type AddonCreditsTier,
} from '@src/service/model/addon/AddonTier'
import type { AddonTierRepository } from '@src/service/worker/repository/AddonTierRepository'

export default class AddonStore {
  readonly addonTierCollection: PersistedCollection<AddonTier, AddonTierRepository>

  private readonly disposeBag = new DisposeBag()

  private fetchAddonTierPromise = new StatefulPromise(this.handleFetchTier.bind(this))
  private hasRefetchAddonTier = false

  constructor(private readonly root: Service) {
    this.addonTierCollection = new PersistedCollection({
      table: root.storage.table('addonTier'),
      classConstructor: (json: AddonTierDataRaw) => new AddonTier(json),
    })

    makeAutoObservable(this, {
      addonTierCollection: observable,
    })

    this.disposeBag.add(this.subscribeToWebSocket())
  }

  async refetchAddonTier() {
    if (!this.hasRefetchAddonTier) {
      this.fetchAddonTierPromise.resetStatus()
      this.hasRefetchAddonTier = true
      await this.fetchAddonTier()
    }
  }

  async fetchAddonTier() {
    if (this.fetchAddonTierPromise.status === 'idle') {
      const response = await this.fetchAddonTierPromise.run()
      void this.addonTierCollection.load(response)
    }
  }

  async updateAddonTier({
    addonId,
    addonTier,
    overagesEnabled,
  }: {
    addonId: string
    addonTier: AddonCreditsTier
    overagesEnabled: boolean
  }) {
    await this.root.transport.billing.updateAddonTier({
      addonId,
      addonTier,
      overagesEnabled,
    })

    const addonTierData = this.addonTierCollection.get(addonId)
    if (addonTierData) {
      addonTierData.localUpdate({ addonTier, overagesEnabled })
    }
  }

  private handleFetchTier() {
    return this.root.transport.billing.getAddonTier(ADDON_IDS.aiAgent)
  }

  /**
   * Addon-tier user properties for Amplitude.
   * Returns `undefined` while the addon tier fetch is idle or in progress.
   * Once resolved (success or failure), returns the tier and overages status
   * with `null` fallbacks if data is unavailable.
   */
  get analyticsProperties(): Record<string, unknown> | undefined {
    if (
      this.fetchAddonTierPromise.status === 'idle' ||
      this.fetchAddonTierPromise.status === 'loading'
    ) {
      return undefined
    }
    const addonTier = this.addonTierCollection.get(ADDON_IDS.aiAgent)
    return {
      automation_credits_tier: addonTier?.addonTier ?? null,
      automation_credit_overages_enabled: addonTier?.overagesEnabled ?? null,
    }
  }

  tearDown() {
    this.addonTierCollection.clear()
  }

  private subscribeToWebSocket() {
    return this.root.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'tiered-usage': {
          const usage = data.usage
          const existingAddonTier = this.addonTierCollection.get(usage.addonId)
          if (existingAddonTier) {
            existingAddonTier.localUpdate({ ...usage })
          } else {
            void this.fetchAddonTier()
          }
          break
        }
        case 'addon-tier-update': {
          const addon = data.addon
          const existingAddonTier = this.addonTierCollection.get(addon.id)
          if (existingAddonTier) {
            // We might need to clear the scheduledNextTier if the response doesn't include it
            const scheduledTier = addon.scheduledNextTier
            existingAddonTier.localUpdate({
              ...addon,
              scheduledNextTier: scheduledTier,
            })
          } else {
            void this.fetchAddonTier()
          }
          break
        }
      }
    })
  }
}
